import { Component, Inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CatalogosService } from '../../../shared/services/catalogos.service';
import { ClaseExistente, CatalogoSATData } from '../../../shared/models';


@Component({
  selector: 'app-formulario-sat',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzSwitchModule,
    NzRadioModule,
    NzGridModule
  ],
  templateUrl: './formulario-sat.component.html',
  styleUrl: './formulario-sat.component.scss'
})
export class FormularioSatComponent implements OnInit {
  satForm!: FormGroup;
  isEdit = false;
  @Input() data: any | undefined;
  private idConceptoSat: number | null = null;
  private codigoInicioClase: string | null = null;
  private codigoFinClase: string | null = null;

  clasesExistentes: ClaseExistente[] = [];

  constructor(
    private fb: FormBuilder,
    private drawerRef: NzDrawerRef,
    private message: NzMessageService,
    private catalogosService: CatalogosService,
  ) {}

  private rangeValidator = (control: any) => {
    if (!control.value || !this.codigoInicioClase || !this.codigoFinClase) {
      return null;
    }
    
    const codigo = control.value.toString().padStart(8, '0');
    const inicio = this.codigoInicioClase.padStart(8, '0');
    const fin = this.codigoFinClase.padStart(8, '0');
    
    if (codigo < inicio || codigo > fin) {
      return { 
        rangeError: { 
          actual: codigo, 
          min: inicio, 
          max: fin 
        } 
      };
    }
    
    return null;
  };

  ngOnInit(): void {
    this.initForm();
    this.loadClasesExistentes();
  }

  private loadClasesExistentes(): void {
    this.catalogosService.obtenerCatalogoSAT().subscribe({
      next: (res) => {
        if (res.success && res.details) {
          const detailsArray = Array.isArray(res.details) ? res.details : [];
          this.clasesExistentes = detailsArray.map((x:any) => {
            delete x.codigosClase;
            return x;
          }) as any[];
        }
      },
      error: (err) => {
        console.error('Error al cargar clases existentes:', err);
      }
    });
  }

  private initForm(): void {
    this.satForm = this.fb.group({
      esRango: [false, [Validators.required]],
      esValido: [true, [Validators.required]],
      descripcion: ['', [Validators.required]],      
      codigoExacto: [''],
      codigoInicio: [''],
      codigoFin: [''],
      usarClaseExistente: [true, [Validators.required]],
      claseId: [null],
      claseCodigoInicio: [''],
      claseCodigoFin: [''],
      claseDescripcion: [''],
      claseCategoria: ['']
    });

    this.setupDynamicValidators();
    
    this.updateCodigoValidators(this.satForm.get('esRango')?.value || false);
    this.updateClaseValidators(this.satForm.get('usarClaseExistente')?.value || true);
    
    if (this.data && !this.data.isNewCode) {
      this.populateFormForEdit();
    }
  }

  private setupDynamicValidators(): void {
    this.satForm.get('esRango')?.valueChanges.subscribe(esRango => {
      this.updateCodigoValidators(esRango);
    });

    this.satForm.get('usarClaseExistente')?.valueChanges.subscribe(usarClaseExistente => {
      this.updateClaseValidators(usarClaseExistente);
      this.updateRangeSource(usarClaseExistente);
    });

    this.satForm.get('claseId')?.valueChanges.subscribe(claseId => {
      if (claseId) {
        this.updateRangeValidators(claseId);
      }
    });

    this.satForm.get('claseCodigoInicio')?.valueChanges.subscribe(() => {
      this.updateRangeFromNewClass();
    });

    this.satForm.get('claseCodigoFin')?.valueChanges.subscribe(() => {
      this.updateRangeFromNewClass();
    });
  }

  private updateCodigoValidators(esRango: boolean): void {
    const codigoExacto = this.satForm.get('codigoExacto');
    const codigoInicio = this.satForm.get('codigoInicio');
    const codigoFin = this.satForm.get('codigoFin');

    const shouldApplyRangeValidator = this.codigoInicioClase && this.codigoFinClase;

    if (esRango) {
      codigoExacto?.clearValidators();
      const validators = shouldApplyRangeValidator ? 
        [Validators.required, this.rangeValidator] : 
        [Validators.required];
      codigoInicio?.setValidators(validators);
      codigoFin?.setValidators(validators);
    } else {
      const validators = shouldApplyRangeValidator ? 
        [Validators.required, this.rangeValidator] : 
        [Validators.required];
      codigoExacto?.setValidators(validators);
      codigoInicio?.clearValidators();
      codigoFin?.clearValidators();
    }

    codigoExacto?.updateValueAndValidity();
    codigoInicio?.updateValueAndValidity();
    codigoFin?.updateValueAndValidity();
  }

  private updateClaseValidators(usarClaseExistente: boolean): void {
    const claseId = this.satForm.get('claseId');
    const claseCodigoInicio = this.satForm.get('claseCodigoInicio');
    const claseCodigoFin = this.satForm.get('claseCodigoFin');
    const claseDescripcion = this.satForm.get('claseDescripcion');
    const claseCategoria = this.satForm.get('claseCategoria');

    if (usarClaseExistente) {
      claseId?.setValidators([Validators.required]);
      claseCodigoInicio?.clearValidators();
      claseCodigoFin?.clearValidators();
      claseDescripcion?.clearValidators();
      claseCategoria?.clearValidators();
    } else {
      claseId?.clearValidators();
      claseCodigoInicio?.setValidators([Validators.required]);
      claseCodigoFin?.setValidators([Validators.required]);
      claseDescripcion?.setValidators([Validators.required]);
      claseCategoria?.setValidators([Validators.required]);
    }

    claseId?.updateValueAndValidity();
    claseCodigoInicio?.updateValueAndValidity();
    claseCodigoFin?.updateValueAndValidity();
    claseDescripcion?.updateValueAndValidity();
    claseCategoria?.updateValueAndValidity();
  }

  private updateRangeValidators(claseId: number): void {
    const claseSeleccionada = this.clasesExistentes.find(c => c.id === claseId);
    
    if (claseSeleccionada) {
      const clase = claseSeleccionada as any;
      this.codigoInicioClase = clase.codigoInicioClase;
      this.codigoFinClase = clase.codigoFinClase;
      
      this.applyRangeValidators();
    }
  }

  private applyRangeValidators(): void {
    const codigoExacto = this.satForm.get('codigoExacto');
    const codigoInicio = this.satForm.get('codigoInicio');
    const codigoFin = this.satForm.get('codigoFin');

    const esRango = this.satForm.get('esRango')?.value;
    
    if (esRango) {
      codigoInicio?.setValidators([Validators.required, this.rangeValidator]);
      codigoFin?.setValidators([Validators.required, this.rangeValidator]);
      codigoExacto?.clearValidators();
    } else {
      codigoExacto?.setValidators([Validators.required, this.rangeValidator]);
      codigoInicio?.clearValidators();
      codigoFin?.clearValidators();
    }

    codigoExacto?.updateValueAndValidity();
    codigoInicio?.updateValueAndValidity();
    codigoFin?.updateValueAndValidity();
  }

  private updateRangeSource(usarClaseExistente: boolean): void {
    if (usarClaseExistente) {
      const claseId = this.satForm.get('claseId')?.value;
      if (claseId) {
        this.updateRangeValidators(claseId);
      }
    } else {
      this.updateRangeFromNewClass();
    }
  }

  private updateRangeFromNewClass(): void {
    const usarClaseExistente = this.satForm.get('usarClaseExistente')?.value;
    if (!usarClaseExistente) {
      const codigoInicio = this.satForm.get('claseCodigoInicio')?.value;
      const codigoFin = this.satForm.get('claseCodigoFin')?.value;
      
      if (codigoInicio && codigoFin) {
        this.codigoInicioClase = codigoInicio;
        this.codigoFinClase = codigoFin;
                
        this.applyRangeValidators();
      } else {
        this.codigoInicioClase = null;
        this.codigoFinClase = null;
        
        this.updateCodigoValidators(this.satForm.get('esRango')?.value || false);
      }
    }
  }

  private populateFormForEdit(): void {
    const editData = this.data;
    this.isEdit = true;
    
    if (editData.clase && editData.codigo) {
      const clase = editData.clase;
      const codigo = editData.codigo;
      
      const esRango = codigo.tipoConcepto === 'RANGO';
      this.idConceptoSat = codigo.id;
      this.codigoInicioClase = clase.codigoInicioClase;
      this.codigoFinClase = clase.codigoFinClase;
      const usarClaseExistente = true;
      
      this.satForm.patchValue({
        esRango: esRango,
        esValido: codigo.esValido !== undefined ? codigo.esValido : true,
        descripcion: codigo.descripcion || '',
        codigoExacto: codigo.codigoExacto || '',
        codigoInicio: codigo.codigoInicio || '',
        codigoFin: codigo.codigoFin || '',
        usarClaseExistente: usarClaseExistente,
        claseId: clase.id
      });

      this.applyRangeValidators();
      
      this.satForm.get('usarClaseExistente')?.disable();
      this.satForm.get('claseId')?.disable();
      this.satForm.get('esRango')?.disable();
      
    } else {
      const esRango = editData.codigoInicio && editData.codigoFin && !editData.codigoExacto;
      const usarClaseExistente = editData.claseId ? true : false;
      
      this.satForm.patchValue({
        esRango: esRango,
        esValido: editData.esValido || true,
        descripcion: editData.descripcion || '',
        codigoExacto: editData.codigoExacto || '',
        codigoInicio: editData.codigoInicio || '',
        codigoFin: editData.codigoFin || '',
        usarClaseExistente: usarClaseExistente,
        claseId: editData.claseId || null,
        claseCodigoInicio: editData.claseCodigoInicio || '',
        claseCodigoFin: editData.claseCodigoFin || '',
        claseDescripcion: editData.claseDescripcion || '',
        claseCategoria: editData.claseCategoria || ''
      });
    }
  }

  onSubmit(): void {
    if (!this.satForm.valid) {
      this.markAllFieldsAsTouched();
      this.message.error('Por favor, completa todos los campos requeridos');
      return;
    }

    let formData = this.satForm.getRawValue();
    let payload = this.buildPayload(formData);
        
    if (this.isEdit) {
      this.catalogosService.actualizarConceptoSAT(payload).subscribe({
        next: (res) => {
          if (res.success) {
            this.message.success('Concepto SAT actualizado correctamente');
            this.drawerRef.close(true);
          } else {
            this.message.error('Error al actualizar concepto SAT');
          }
        },
        error: (err) => {
          console.error('Error al actualizar concepto SAT:', err);
          this.message.error('Error al actualizar concepto SAT');
        }
      });
    } else {
      this.catalogosService.crearConceptoSAT(payload).subscribe({
        next: (res) => {
          if (res.success) {
            this.message.success('Concepto SAT creado correctamente');
            this.drawerRef.close(true);
          } else {
            this.message.error('Error al crear concepto SAT');
          }
        },
        error: (err) => {
          console.error('Error al crear concepto SAT:', err);
          this.message.error('Error al crear concepto SAT');
        }
      });
    }
  }

  private buildPayload(formData: any): any {
    const { usarClaseExistente, esRango, ...baseData } = formData;
    
    let payload: any = {
      esRango: esRango,
      esValido: baseData.esValido,
      descripcion: baseData.descripcion
    };

    if (this.isEdit && this.data.codigo.id) {
      payload.idConceptoSat = this.data.codigo.id;
    }

    if (esRango) {
      payload.codigoInicio = baseData.codigoInicio;
      payload.codigoFin = baseData.codigoFin;
    } else {
      payload.codigoExacto = baseData.codigoExacto;
    }

    if (usarClaseExistente) {
      payload.claseId = baseData.claseId;
    } else {
      payload.claseCodigoInicio = baseData.claseCodigoInicio;
      payload.claseCodigoFin = baseData.claseCodigoFin;
      payload.claseDescripcion = baseData.claseDescripcion;
      payload.claseCategoria = baseData.claseCategoria;
    }

    return payload;
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.satForm.controls).forEach(key => {
      this.satForm.get(key)?.markAsTouched();
    });
  }

  get isFormValid(): boolean {
    return this.satForm.valid;
  }

  get esRango(): boolean {
    return this.satForm.get('esRango')?.value || false;
  }

  get usarClaseExistente(): boolean {
    return this.satForm.get('usarClaseExistente')?.value || true;
  }
}
